import { create } from 'zustand';
import { useCurrentProjectStore } from './current-project';

export interface AuthProject {
  project_id: string;
  project_public_id: string;
  project_name: string;
  owner_company_id: string;
  effective_role: 'pa' | 'engineer' | 'lead' | 'admin' | 'super_admin';
}

export interface AuthUser {
  id: string;
  email: string;
  display_name: string;
  is_super_admin: boolean;
  company_admin: boolean;
  company_id: string;
  avatar_url?: string | null;
}

interface AuthState {
  access: string | null;
  user: AuthUser | null;
  bootstrapping: boolean;
  reauthRequired: boolean;
  setAccess: (token: string | null) => void;
  setUser: (user: AuthUser | null) => void;
  setBootstrapped: () => void;
  setReauthRequired: (v: boolean) => void;
  clear: () => void;
}

// User-scoped UI state that must not leak across login boundaries. The
// current-project switcher persists to localStorage so the URL "remembers"
// which project a user was last looking at, but that's per-user — if a
// different account signs in (or the same browser is reused after logout),
// the prior project id must be dropped or the new user will be silently
// redirected into a project they can't access.
function resetUserScopedUiState(): void {
  useCurrentProjectStore.getState().setCurrentProjectId(null);
}

export const useAuthStore = create<AuthState>((set, get) => ({
  access: null,
  user: null,
  bootstrapping: true,
  reauthRequired: false,
  setAccess: (access) => set({ access }),
  setUser: (user) => {
    const prev = get().user;
    if (user && prev && prev.id !== user.id) {
      resetUserScopedUiState();
    }
    set({ user });
  },
  setBootstrapped: () => set({ bootstrapping: false }),
  // Intentionally nulls `access` when reauthRequired becomes true: with no
  // access token, in-flight and new requests 401 immediately and get parked
  // on the reauth gate (handleResponseError) until the user logs back in.
  setReauthRequired: (reauthRequired) =>
    set(reauthRequired ? { reauthRequired, access: null } : { reauthRequired }),
  clear: () => {
    resetUserScopedUiState();
    set({ access: null, user: null, bootstrapping: false, reauthRequired: false });
  },
}));
